import React, { useState, useEffect } from 'react';
import { Box, Container } from '@mui/material';
import QuizWelcome from './QuizWelcome';
import QuizConfig from './normalMode/QuizConfig';
import QuizRules from './normalMode/QuizRules';
import QuizletScreen from './normalMode/QuizletScreen';
import QuizQuestion from './normalMode/QuizQuestion';
import QuizResults from './normalMode/QuizResults';
import QuizErrorBoundary from './QuizErrorBoundary';
import GuestSignupModal from '../auth/GuestSignupModal';
import AuthModal from '../auth/AuthModal';
import LiveQuizWelcome from './liveMode/LiveQuizWelcome';
import RapidQuizWelcome from './rapidMode/RapidQuizWelcome';
import FlashQuizWelcome from './flashMode/FlashQuizWelcome';
import FlashQuizPracticeWelcome from './flashMode/FlashQuizPracticeWelcome';
import { useQuizConfig } from '../../hooks/useQuizConfig';
import { useQuizState } from '../../hooks/useQuizState';
import { useUserTracking } from '../../hooks/useUserTracking';
import { useAuthContext } from '../../../context/AuthContext';
import { useCountdown } from './normalMode/useCountdown';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getGuestDisplayName } from '../../../utils/guestDisplayUtils';

const QUIZ_STEPS = {
  WELCOME: 'welcome',
  CONFIG: 'config',
  RULES: 'rules',
  QUIZLET: 'quizlet',
  QUIZ: 'quiz',
  RESULTS: 'results'
};

const QuizMaster = () => {
  const {
    state,
    updateState,
    clearState,
    resumeQuiz
  } = useQuizState();

  const {
    config,
    setConfig,
    saveConfig,
    loadConfig,
    clearConfig
  } = useQuizConfig();

  const {
    createQuizSession,
    trackQuizEvent,
    createQuizlet,
    createQuizletResult,
    trackQuizletEvent,
    updateQuizSession
  } = useUserTracking();

  const { user, isAuthenticated } = useAuthContext();
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [finalResults, setFinalResults] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const quizMode = searchParams.get('quizMode'); // e.g. /quiz?quizMode=flash
  const forceJoin = searchParams.get('forceJoin') === '1';
  const practiceParam = searchParams.get('practice') === '1';

  // Main timer management - centralized
  const mainTimerDuration = (config?.duration_minutes || 5) * 60; // Convert minutes to seconds
  const {
    timeLeft: mainTimeLeft,
    start: startMainTimer,
    stop: stopMainTimer,
    reset: resetMainTimer,
    isRunning: mainTimerRunning
  } = useCountdown({
    initialTime: mainTimerDuration,
    onComplete: handleMainTimerComplete
  });

  async function handleMainTimerComplete() {
    // console.log('[QuizMaster] Main timer expired - forcing quiz completion');

    // Stop and reset all timers when main timer expires
    // console.log('[QuizMaster] Stopping and resetting all timers (main timer expired)');
    stopMainTimer();
    resetMainTimer();

    // Flatten all question results for the results page
    const allQuestionResults = Object.entries(state.quizletResults || {}).flatMap(([quizletIndex, qr]) =>
      (qr.questionResults || []).map((questionResult, questionIndex) => ({
        ...questionResult,
        songIndex: parseInt(quizletIndex),
        questionIndex: questionIndex
      }))
    );

    // Force complete the quiz when main timer expires
    const finalResults = {
      total_score: Object.values(state.quizletResults || {}).reduce((total, qr) => total + (qr.totalScore || 0), 0),
      totalScore: Object.values(state.quizletResults || {}).reduce((total, qr) => total + (qr.totalScore || 0), 0), // Keep both for compatibility
      quizletResults: state.quizletResults || {},
      question_results: allQuestionResults, // Add flattened question results
      totalQuestions: Object.values(state.quizletResults || {}).reduce((total, qr) => total + (qr.totalQuestions || 0), 0),
      correctAnswers: Object.values(state.quizletResults || {}).reduce((total, qr) => total + (qr.correctAnswers || 0), 0),
      completedSongs: state.completedQuizlets?.size || 0,
      totalSongs: state.quizData?.quiz_data?.length || 0,
      config: config,
      completion_time: mainTimerDuration, // Full duration when timer expires
      mainTimerExpired: true
    };

    // Track quiz completion when timer expires
    await trackQuizEvent('quiz_complete', { results: finalResults });
    await updateQuizSession({ 
      status: 'completed', 
      completed_at: new Date().toISOString() 
    });

    updateState({
currentStep: QUIZ_STEPS.RESULTS,
      quizResults: finalResults
    });
  }

  // Initialize state from localStorage on mount
  useEffect(() => {
    const savedState = resumeQuiz();
    if (savedState && savedState.currentStep !== 'welcome') {
      // Resume from saved state
      if (savedState.config) {
        setConfig(savedState.config);
      }
    } else {
      // Start fresh
      loadConfig();
    }
  }, [resumeQuiz, setConfig, loadConfig]);

  // Insta Quiz (flash): skip generic QuizWelcome — FlashQuizWelcome checks session and shows
  // No Active page or the join welcome when a session is live.
  useEffect(() => {
    if (quizMode === 'flash' && !practiceParam && state.currentStep === QUIZ_STEPS.WELCOME) {
      setSelectedMode('flash');
      updateState({ currentStep: 'flash-welcome' });
    }
  }, [quizMode, practiceParam, state.currentStep, updateState]);

  // When practice=1 + quizMode=flash, go straight to practice mode
  useEffect(() => {
    if (practiceParam && quizMode === 'flash' && state.currentStep === QUIZ_STEPS.WELCOME) {
      setSelectedMode('flash');
      updateState({ currentStep: 'flash-practice' });
    }
  }, [practiceParam, quizMode, state.currentStep, updateState]);

  // Save config to localStorage whenever it changes
  useEffect(() => {
    if (config.difficulty_id) {
      saveConfig();
      updateState({ config });
    }
  }, [config, saveConfig, updateState]);

  const handleModeSelect = async (mode) => {
    if (mode === 'normal') {
      // Store mode selection for later tracking
      setSelectedMode(mode);
      updateState({ currentStep: QUIZ_STEPS.CONFIG });
    } else if (mode === 'live') {
      setSelectedMode(mode);
      updateState({ currentStep: 'live-welcome' });
    } else if (mode === 'rapid') {
      setSelectedMode(mode);
      updateState({ currentStep: 'rapid-welcome' });
    } else if (mode === 'flash') {
      setSelectedMode(mode);
      updateState({ currentStep: 'flash-welcome' });
    } else if (mode === 'flash-practice') {
      setSelectedMode('flash');
      updateState({ currentStep: 'flash-practice' });
    }
  };

  const handleLiveQuizJoin = async (session, participantData) => {
    // Parse session configuration
    const sessionConfig = typeof session.quiz_config === 'string' 
      ? JSON.parse(session.quiz_config) 
      : session.quiz_config;
    
    // Convert live quiz config to normal quiz config format
    // Handle both old format (difficulty string) and new format (difficulty_id)
    let difficultyId = sessionConfig.difficulty_id;
    
    // If using old format, map difficulty strings to IDs
    if (!difficultyId && sessionConfig.difficulty) {
      const difficultyMap = {
        'easy': 6,
        'medium': 7, 
        'hard': 8,
        'very_hard': 9
      };
      difficultyId = difficultyMap[sessionConfig.difficulty] || 7; // Default to medium (ID 7)
    }
    
    const quizConfig = {
      difficulty_id: difficultyId || 7, // Default to medium (ID 7) if no difficulty specified
      topic_id: '', // No specific topic for live quiz - will generate random songs
      duration_minutes: sessionConfig.duration || 5, // Use actual duration
      questions_per_song: sessionConfig.questions_per_song || 3, // Use stored questions per song directly
      number_of_songs: sessionConfig.number_of_songs || 20
    };
    
    // console.log('Live Quiz Config:', sessionConfig);
    // console.log('Generated Quiz Config:', quizConfig);
    
    // Validate configuration before making API call
    if (!quizConfig.difficulty_id || !quizConfig.duration_minutes || !quizConfig.questions_per_song) {
      throw new Error('Invalid quiz configuration: missing required fields');
    }
    
    if (quizConfig.questions_per_song < 1) {
      throw new Error('Invalid quiz configuration: questions per song must be at least 1');
    }
    
    try {
      // Generate quiz data based on session configuration
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/quiz/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quizConfig),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate quiz data');
      }
      
      const generatedQuizData = await response.json();
      
      setConfig(quizConfig);
      updateState({ 
        currentStep: QUIZ_STEPS.RULES,
        quizData: generatedQuizData,
        liveMode: true,
        liveSessionId: session.session_id,
        participantId: participantData.participant_id
      });
    } catch (error) {
      console.error('Error generating quiz data for live session:', error);
      // Fallback: try to use empty quiz data and show error
      setConfig(quizConfig);
      updateState({ 
        currentStep: QUIZ_STEPS.RULES,
        quizData: { quiz_data: [] },
        liveMode: true,
        liveSessionId: session.session_id,
        participantId: participantData.participant_id,
        error: 'Failed to load quiz questions. Please try again.'
      });
    }
  };

  const handleRapidQuizJoin = async (session, participantData) => {
    // RapidQuizWelcome now handles its own flow with RapidQuizMain
    // This function is kept for compatibility but doesn't need to do anything
    // console.log('Rapid quiz join handled by RapidQuizWelcome component');
  };

  const handleBackToWelcome = () => {
    if (quizMode === 'flash') {
      navigate('/mindyourmusic');
      return;
    }
    setSelectedMode(null);
    updateState({ currentStep: QUIZ_STEPS.WELCOME });
  };

  const handleConfigComplete = async (configData, fetchedQuizData) => {
    try {
      // Create quiz session and get the session ID
      const newSessionId = await createQuizSession(configData, fetchedQuizData);
      
      // Track mode selection (now that session exists)
      if (selectedMode) {
        await trackQuizEvent('quiz_mode_selected', { mode: selectedMode }, newSessionId);
      }
      
      // Track config completion
      await trackQuizEvent('quiz_config_next', { config: configData }, newSessionId);
      
      setConfig(configData);
      updateState({
        currentStep: QUIZ_STEPS.RULES,
        config: configData,
        quizData: fetchedQuizData
      });
    } catch (error) {
      console.error('Error creating quiz session:', error);
      // Fallback to original behavior if tracking fails
      setConfig(configData);
      updateState({
        currentStep: QUIZ_STEPS.RULES,
        config: configData,
        quizData: fetchedQuizData
      });
    }
  };

  const handleRulesComplete = async () => {
    await trackQuizEvent('quiz_rules_next');
    updateState({ currentStep: QUIZ_STEPS.QUIZLET });
  };

  const handleQuizletStart = async (quizletIndex, finalResults = null) => {
    // Handle completion case
    if (quizletIndex === 'complete' && finalResults) {
      // Track quiz completion
      await trackQuizEvent('quiz_complete', { results: finalResults });
      await updateQuizSession({ 
        status: 'completed', 
        completed_at: new Date().toISOString() 
      });

      // Stop and reset all timers when quiz is completed
      stopMainTimer();
      resetMainTimer();

      // Show appropriate modal based on user authentication status
      // console.log('Quiz completion - Authentication check:');
      // console.log('isAuthenticated:', isAuthenticated);
      // console.log('user:', user);
      // console.log('user?.user_type:', user?.user_type);
      
      if (!isAuthenticated || !user) {
        // console.log('User not authenticated, showing auth modal');
        // Not authenticated at all - show full auth modal
        setFinalResults(finalResults);
        setShowAuthModal(true);
      } else if (user?.user_type === 'guest') {
        // console.log('User is guest, showing signup modal');
        // Guest user - show signup-only modal
        setFinalResults(finalResults);
        setShowSignupModal(true);
      } else {
        // console.log('User is registered, going to results page');
        // Registered user - go directly to results
        updateState({
          currentStep: QUIZ_STEPS.RESULTS,
          quizResults: finalResults,
          currentQuizletIndex: null
        });
      }
      return;
    }

    try {
      // Create quizlet in database
      const currentSong = state.quizData?.quiz_data?.[quizletIndex];
      await createQuizlet(
        currentSong?.song?.songs_master_id,
        quizletIndex,
        currentSong,
        currentSong?.questions?.length
      );

      // Track quizlet start
      await trackQuizletEvent(quizletIndex, 'quizlet_start', {
        quizlet_index: quizletIndex,
        song_name: currentSong?.song?.name
      });

      // Track quiz started event (only on first quizlet)
      if (quizletIndex === 0 && typeof quizletIndex === 'number') {
        await trackQuizEvent('quiz_started', { 
          quizlet_index: quizletIndex,
          song_name: currentSong?.song?.name 
        });
      }

      // Start main timer on first quizlet start
      if (!mainTimerRunning && quizletIndex !== 'complete') {
        startMainTimer();
      }

      // Start individual quizlet
      updateState({
        currentStep: QUIZ_STEPS.QUIZ,
        currentQuizletIndex: quizletIndex
      });
    } catch (error) {
      console.error('Error creating quizlet:', error);
      // Fallback to original behavior
      if (!mainTimerRunning && quizletIndex !== 'complete') {
        startMainTimer();
      }
      updateState({
        currentStep: QUIZ_STEPS.QUIZ,
        currentQuizletIndex: quizletIndex
      });
    }
  };

  const handleQuizComplete = async (quizletIndex, quizletResult) => {
    try {
      // Create quizlet result in database
      await createQuizletResult(quizletIndex, {
        total_score: quizletResult.totalScore,
        correct_answers: quizletResult.correctAnswers,
        total_questions: quizletResult.totalQuestions,
        percentage: quizletResult.percentage,
        time_taken_seconds: quizletResult.timeTaken,
        quizlet_start_time: new Date(Date.now() - quizletResult.timeTaken * 1000).toISOString(),
        quizlet_end_time: new Date().toISOString(),
        timed_out: quizletResult.timedOut ? 1 : 0,
        question_results: quizletResult.questionResults
      });

      // Track quizlet end
      await trackQuizletEvent(quizletIndex, 'quizlet_end', {
        quizlet_index: quizletIndex,
        score: quizletResult.totalScore
      });
    } catch (error) {
      console.error('Error tracking quizlet completion:', error);
    }

    // Update the quizlet results in state
    const currentCompleted = state.completedQuizlets || new Set();
    const newCompletedQuizlets = new Set([...currentCompleted, quizletIndex]);
    const updatedQuizletResults = {
      ...state.quizletResults,
      [quizletIndex]: quizletResult
    };

    // Check if all quizlets are completed
    const totalSongs = state.quizData?.quiz_data?.length || 0;
    const isAllCompleted = newCompletedQuizlets.size >= totalSongs;

    if (isAllCompleted) {
      // Stop and reset all timers when quiz is completed
      stopMainTimer();
      resetMainTimer();

      // Flatten all question results for the results page
      const allQuestionResults = Object.entries(updatedQuizletResults).flatMap(([quizletIndex, qr]) =>
        (qr.questionResults || []).map((questionResult, questionIndex) => ({
          ...questionResult,
          songIndex: parseInt(quizletIndex),
          questionIndex: questionIndex
        }))
      );

      // Calculate final results
      const finalResults = {
        total_score: Object.values(updatedQuizletResults).reduce((total, qr) => total + (qr.totalScore || 0), 0),
        totalScore: Object.values(updatedQuizletResults).reduce((total, qr) => total + (qr.totalScore || 0), 0), // Keep both for compatibility
        quizletResults: updatedQuizletResults,
        question_results: allQuestionResults, // Add flattened question results
        totalQuestions: Object.values(updatedQuizletResults).reduce((total, qr) => total + (qr.totalQuestions || 0), 0),
        correctAnswers: Object.values(updatedQuizletResults).reduce((total, qr) => total + (qr.correctAnswers || 0), 0),
        completedSongs: newCompletedQuizlets.size,
        totalSongs: totalSongs,
        config: config,
        completion_time: mainTimerDuration - mainTimeLeft, // Add completion time
        mainTimerExpired: false
      };

      // Track quiz completion when all quizlets are finished
      await trackQuizEvent('quiz_complete', { results: finalResults });
      await updateQuizSession({ 
        status: 'completed', 
        completed_at: new Date().toISOString() 
      });

      // Show appropriate modal based on user authentication status
      // console.log('Quiz completion (all quizlets) - Authentication check:');
      // console.log('isAuthenticated:', isAuthenticated);
      // console.log('user:', user);
      // console.log('user?.user_type:', user?.user_type);
      
      if (!isAuthenticated || !user) {
        // console.log('User not authenticated, showing auth modal');
        // Not authenticated at all - show full auth modal
        setFinalResults(finalResults);
        setShowAuthModal(true);
      } else if (user?.user_type === 'guest') {
        // console.log('User is guest, showing signup modal');
        // Guest user - show signup-only modal
        setFinalResults(finalResults);
        setShowSignupModal(true);
      } else {
        // console.log('User is registered, going to results page');
        // Registered user - go directly to results
        updateState({
          currentStep: QUIZ_STEPS.RESULTS,
          quizResults: finalResults,
          completedQuizlets: newCompletedQuizlets,
          quizletResults: updatedQuizletResults,
          currentQuizletIndex: null
        });
      }
    } else {
      // Return to quizlet screen for next song
      updateState({
        currentStep: QUIZ_STEPS.QUIZLET,
        completedQuizlets: newCompletedQuizlets,
        quizletResults: updatedQuizletResults,
        currentQuizletIndex: null
      });
    }
  };

  const handleReplayQuiz = () => {
    // Stop and reset all timers when replaying quiz
    // console.log('[QuizMaster] Stopping and resetting all timers (replay quiz)');
    stopMainTimer();
    resetMainTimer();

    updateState({
      currentStep: QUIZ_STEPS.RULES,
      quizResults: null
    });
  };

  const handleGoHome = () => {
    // Stop and reset all timers when going home
    // console.log('[QuizMaster] Stopping and resetting all timers (go home)');
    stopMainTimer();
    resetMainTimer();

    clearConfig();
    clearState();
  };

  const handleBackToConfig = () => {
    updateState({ currentStep: QUIZ_STEPS.CONFIG });
  };

  // Signup modal handlers
  const handleSignupSuccess = (registeredUser) => {
    // console.log('User successfully registered:', registeredUser);
    setShowSignupModal(false);
    updateState({
      currentStep: QUIZ_STEPS.RESULTS,
      quizResults: finalResults,
      currentQuizletIndex: null
    });
  };

  const handleContinueGuest = () => {
    setShowSignupModal(false);
    updateState({
      currentStep: QUIZ_STEPS.RESULTS,
      quizResults: finalResults,
      currentQuizletIndex: null
    });
  };

  const handleSkipSignup = () => {
    setShowSignupModal(false);
    updateState({
      currentStep: QUIZ_STEPS.RESULTS,
      quizResults: finalResults,
      currentQuizletIndex: null
    });
  };

  // Auth modal handlers
  const handleAuthSuccess = (authenticatedUser) => {
    // console.log('User successfully authenticated:', authenticatedUser);
    setShowAuthModal(false);
    updateState({
      currentStep: QUIZ_STEPS.RESULTS,
      quizResults: finalResults,
      currentQuizletIndex: null
    });
  };

  const handleAuthModalClose = () => {
    setShowAuthModal(false);
    updateState({
      currentStep: QUIZ_STEPS.RESULTS,
      quizResults: finalResults,
      currentQuizletIndex: null
    });
  };

  const isFlashWelcomeStep =
    state.currentStep === 'flash-welcome' ||
    (quizMode === 'flash' && !practiceParam && state.currentStep === QUIZ_STEPS.WELCOME);

  const isInstaQuizFullBleed =
    (quizMode === 'flash' && !practiceParam && isFlashWelcomeStep) ||
    state.currentStep === 'flash-practice';

  return (
    <QuizErrorBoundary>
      <Box sx={{
        minHeight: '100vh',
        bgcolor: isInstaQuizFullBleed ? 'transparent' : 'background.default',
        py: isInstaQuizFullBleed ? 0 : 2,
      }}>
        {isInstaQuizFullBleed ? (
          <>
            {isFlashWelcomeStep && (
              <FlashQuizWelcome
                onBack={handleBackToWelcome}
                forceJoin={forceJoin}
                onPractice={() => updateState({ currentStep: 'flash-practice' })}
              />
            )}
            {state.currentStep === 'flash-practice' && (
              <FlashQuizPracticeWelcome
                onBack={handleBackToWelcome}
                onPlayLive={() => updateState({ currentStep: 'flash-welcome' })}
              />
            )}
          </>
        ) : (
        <Container maxWidth="lg">
          {state.currentStep === QUIZ_STEPS.WELCOME && quizMode !== 'flash' && (
            <QuizWelcome
              onSelectMode={handleModeSelect}
              quizMode={quizMode}
            />
          )}

          {state.currentStep === 'live-welcome' && (
            <LiveQuizWelcome
              onBack={handleBackToWelcome}
              onJoinQuiz={handleLiveQuizJoin}
            />
          )}

          {state.currentStep === 'rapid-welcome' && (
            <RapidQuizWelcome
              onBack={handleBackToWelcome}
              onJoinQuiz={handleRapidQuizJoin}
            />
          )}

          {state.currentStep === QUIZ_STEPS.CONFIG && (
            <QuizConfig
              onComplete={handleConfigComplete}
              initialConfig={config}
              onBack={handleBackToWelcome}
            />
          )}

          {state.currentStep === QUIZ_STEPS.RULES && (
            <QuizRules
              config={config}
              quizData={state.quizData}
              onStart={handleRulesComplete}
              onBack={state.liveMode ? null : handleBackToConfig}
              isLiveMode={state.liveMode || false}
            />
          )}

          {state.currentStep === QUIZ_STEPS.QUIZLET && (
            <QuizletScreen
              config={config}
              quizData={state.quizData}
              onStartQuiz={handleQuizletStart} 
              completedQuizlets={state.completedQuizlets instanceof Set ? state.completedQuizlets : new Set(state.completedQuizlets || [])}
              quizletResults={state.quizletResults || {}}
              mainTimer={{
                timeLeft: mainTimeLeft,
                isRunning: mainTimerRunning,
                duration: mainTimerDuration
              }}
            />
          )}

          {state.currentStep === QUIZ_STEPS.QUIZ && (
            <QuizQuestion
              config={config}
              quizData={state.quizData}
              currentQuizletIndex={state.currentQuizletIndex}
              onComplete={handleQuizComplete}
              mainTimer={{
                timeLeft: mainTimeLeft,
                isRunning: mainTimerRunning,
                duration: mainTimerDuration
              }}
            />
          )}

          {state.currentStep === QUIZ_STEPS.RESULTS && (
            <QuizResults
              config={config}
              quizData={state.quizData}
              results={state.quizResults}
              onReplay={handleReplayQuiz}
              onGoHome={handleGoHome}
            />
          )}
        </Container>
        )}
      </Box>

      {/* Guest Signup Modal for Guest Users */}
      <GuestSignupModal
        open={showSignupModal}
        onClose={() => setShowSignupModal(false)}
        onSignup={handleSignupSuccess}
        onContinueGuest={handleContinueGuest}
        onSkip={handleSkipSignup}
        userScore={finalResults?.totalScore || 0}
        guestName={getGuestDisplayName(user)}
      />

      {/* Auth Modal for Non-Authenticated Users */}
      <AuthModal
        open={showAuthModal}
        onClose={handleAuthModalClose}
        onSuccess={handleAuthSuccess}
      />
    </QuizErrorBoundary>
  );
};

export default QuizMaster;
